import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useReactFlow } from "@xyflow/react";
import {
  canvasAtom,
  edgesAtom,
  isDraftModeExcludingTemplatesAtom,
  nodesAtom,
  startStreamingProgressSetter,
  togglesAtomResettable,
  zslAtom,
  isTourableNode,
  isDraftModeIncludingTemplatesAtom,
} from "@zapier/canvas-state";
import { CopilotProvider, useCopilotRegistry } from "@zapier/ai-toolkit/client";
import { parseCanvasDataFromZSL } from "@zapier/streaming";
import { NODE_TYPES_TO_EXCLUDE_FROM_TOUR } from "@zapier/canvas-constants";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ZslSchemaForApi } from "@zapier/zsl-creator-schemas";
import { z } from "zod";
import { Tool } from "ai";
import {
  aiPromptAtom,
  outlineAtom,
  partialOutlineAtom,
} from "@zapier/streaming";

import { renderAnnotationNodesSetter } from "../../tools/tour/renderAnnotationNodesSetter";
import { adaptCanvasToolsToAiToolkit } from "./utils/aiToolkitAdapter";
import { getCanvasCopilotRegistry } from "../../utils/canvasCopilotRegistry";
import { buildModeSuggestionsClientTool } from "../../tools/buildModeTools/buildModeSuggestionsClient";
import { nodeDescription, tourDescription } from "../../tools/tour/tourContext";

import { useSubmitAndWait } from "./useSubmitAndWait";

// Constants for tour description character limits
const DESCRIPTION_MIN_CHARS = 100;
const DESCRIPTION_MAX_CHARS = 144;

export const tourStepSchema = z.object({
  header: z.string(),
  description: z.string().min(DESCRIPTION_MIN_CHARS).max(DESCRIPTION_MAX_CHARS),
  parentId: z.string(),
  stepNumber: z.number().int().min(0),
});

export type ToolWithInclude = Tool & {
  shouldInclude?: () => boolean;
};

/**
 * CanvasCopilotHydrator is placed as a child of CanvasHydrator to ensure immediate access to:
 * - zsl data
 * - canvas metadata
 * - node data
 * - edge data
 *
 * This placement prevents any data transition states (like placeholder to real data)
 * and ensures the copilot has all required data from initial render.
 */
export function CanvasCopilotHydrator({
  children,
}: {
  children: React.ReactNode;
}) {
  const { fitView, setViewport } = useReactFlow();
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const [zsl, setZsl] = useAtom(zslAtom);
  const [canvas, setCanvas] = useAtom(canvasAtom);
  const setOutline = useSetAtom(outlineAtom);
  const setPartialOutline = useSetAtom(partialOutlineAtom);
  const startProgressAnimation = useSetAtom(startStreamingProgressSetter);
  const isDraftModeNotTemplates = useAtomValue(
    isDraftModeExcludingTemplatesAtom,
  );
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const toggles = useAtomValue(togglesAtomResettable);
  const renderAnnotationNodes = useSetAtom(renderAnnotationNodesSetter);
  const prompt = useAtomValue(aiPromptAtom);

  // Build mode suggestions setup (includes field mapping and other categories)
  const { registry } = useCopilotRegistry(getCanvasCopilotRegistry);

  const shouldShowBuildModeSuggestions = (): boolean => {
    return !isDraftModeIncludingTemplates;
  };

  // Calculate filtered nodes for tour description
  const filteredNodesForTour = nodes.filter(isTourableNode);

  // Smart function to determine if we should use filtered nodes
  const getNodesForContext = () => {
    const hasAnnotationNodes = nodes.some((node) => node.type === "annotation");

    if (hasAnnotationNodes) {
      // When annotation nodes exist, we're in a tour context
      return filteredNodesForTour;
    }

    // No annotation nodes = no tour context, use all nodes
    return nodes;
  };

  const submitAndWait = useSubmitAndWait({
    api: "/api/zsl/v2/combined_stream",
    onError: (error: string) => console.error("Error:", error),
  });

  const resetCanvas = () => {
    setNodes([]);
    setCanvas((prev) => ({
      ...prev,
      nodes: [],
      edges: [],
    }));
    setOutline("");
    setPartialOutline("");
    setEdges([]);
    setZsl(undefined);
    setViewport({ x: 0, y: 0, zoom: 1 });
  };

  /**
   * @deprecated Please add new tools to toolsAtom instead
   * We will slowly move all the tools here over there too.
   */
  const canvasTools: Record<string, ToolWithInclude> = {
    edit_system: {
      description: `This tool is used to:
        - Edit table, interface, and chatbot assets.
        - Add new tables and interfaces.
        - Remove any assets.

        ## Important
        This tool should not be used to:
        - Edit or add zaps.
        - Existing zaps MUST be preserved unchanged in your response.

        When calling this tool, make sure to provide the entire new system including:
        1. Your edits to tables, interfaces, or chatbots
        2. All existing zaps exactly as they are (do not modify or omit them)
        3. Any other existing components that should be preserved

        Your response must contain the complete system state, not just the changed parts.
        `,
      parameters: ZslSchemaForApi,
      shouldInclude: () => isDraftModeNotTemplates,
      execute: async (args: any) => {
        setZsl(args);
        const nodesAndEdges = parseCanvasDataFromZSL(args, {
          canvas: {
            name: canvas.name || "Untitled Canvas",
            description: canvas.description || "",
            nodes: nodes as any,
            edges: edges as any,
          },
          warnings: [],
        });
        if (nodesAndEdges) {
          const {
            canvas: { nodes, edges },
          } = nodesAndEdges;
          setNodes(nodes as any);
          setEdges(edges as any);
          fitView();
        }
        return {
          content: [{ type: "text", text: "Canvas updated" }],
        };
      },
    },
    create_system: {
      description: ` The result of this tool will replace the current system.

        ## ALWAYS USE THIS TOOL FOR:
        - ANY changes involving zaps (new or edited)
        - Larger or complex system changes
        - Creating new systems
        - When edit_system is failing

        ## When NOT to use this tool:
        - Simple edits to fields on tables, interfaces, or chatbots
        - Adding new fields to existing tables, interfaces, or chatbots
        - Removing fields from tables, interfaces, or chatbots
        - Simple addition or removal of tables, interfaces, or chatbots that don't affect zaps
        
        Note: This tool completely replaces the system state with a new version created from your prompt.
        `,
      parameters: z.object({
        prompt: z
          .string()
          .describe(
            "A improved version of the users prompt to clarify users intent. This prompt will be used to update the system by another agent ",
          ),
      }),

      shouldInclude: () => isDraftModeNotTemplates,
      execute: async (args: any) => {
        resetCanvas();
        startProgressAnimation();
        const zsl = await submitAndWait(args.prompt);
        // Reset streaming progress after successful completion
        return {
          content: [
            {
              type: "text",
              text: `System updated with zsl: ${JSON.stringify(zsl)}`,
            },
          ],
        };
      },
    },
    tour: {
      description: tourDescription(
        nodes,
        NODE_TYPES_TO_EXCLUDE_FROM_TOUR,
        DESCRIPTION_MIN_CHARS,
        DESCRIPTION_MAX_CHARS,
      ),
      parameters: z.object({
        rawTourStepsData: z.array(tourStepSchema),
      }),
      shouldInclude: () => true,
      execute: async ({ rawTourStepsData }) => {
        renderAnnotationNodes({
          rawTourStepsData,
          fitView,
        });
        return {
          content: [
            {
              type: "text",
              text: "Tour started! Use the intro panel to begin.",
            },
          ],
        };
      },
    },
    explain_node: {
      description: nodeDescription(
        DESCRIPTION_MIN_CHARS,
        DESCRIPTION_MAX_CHARS,
      ),
      parameters: z.object({
        header: z
          .string()
          .describe(
            "A clear, descriptive header for the explanation in sentence case",
          ),
        description: z
          .string()
          .min(DESCRIPTION_MIN_CHARS)
          .max(DESCRIPTION_MAX_CHARS)
          .describe(
            "A detailed explanation of what this node does and its role in the workflow",
          ),
      }),
      shouldInclude: () => true,
      execute: async ({ header, description }) => {
        renderAnnotationNodes({
          rawTourStepsData: [
            {
              header,
              description,
              parentId: "", // Will be resolved in renderAnnotationNodesSetter from nodeIdToExplainAtom
              stepNumber: 1,
            },
          ],
          fitView,
        });

        return {
          content: [
            {
              type: "text",
              text: "Explanation created for the step! The annotation shows details about this node's purpose and role in your workflow.",
            },
          ],
        };
      },
    },
    show_build_mode_suggestions: {
      ...buildModeSuggestionsClientTool(),
      shouldInclude: shouldShowBuildModeSuggestions,
    },
  };

  const filteredTools = (
    tools: Record<string, ToolWithInclude | Tool>,
  ): Record<string, Tool> => {
    return Object.fromEntries(
      Object.entries(tools).filter(([, tool]) => {
        let shouldIncludeTool = true;
        if ("shouldInclude" in tool && tool.shouldInclude) {
          shouldIncludeTool = tool.shouldInclude();
        }
        return shouldIncludeTool;
      }),
    );
  };
  // Generate dynamic initial messages based on URL prompt parameter
  const generateInitialMessages = () => {
    // If there's a prompt from the URL, create a user message with that prompt
    if (prompt && typeof prompt === "string" && prompt.trim()) {
      const decodedPrompt = decodeURIComponent(prompt.trim());
      return [
        {
          id: "canvas-welcome",
          role: "assistant" as const,
          content: decodedPrompt,
        },
      ];
    }

    // Show build mode suggestions when there's actual canvas content
    if (shouldShowBuildModeSuggestions()) {
      return [
        {
          id: "canvas-welcome-build-mode",
          role: "assistant" as const,
          content:
            "I'm here to help you build and edit zapier workflows on your canvas. I can help you create systems using zaps, tables, interfaces, and chatbots. I can also edit existing components and work with Zaps.\n\nHere are some quick actions to get started:",
        },
        {
          id: "canvas-show-build-suggestions",
          role: "assistant" as const,
          content: "",
          toolInvocations: [
            {
              toolCallId: "show-build-suggestions-initial",
              toolName: "show_build_mode_suggestions",
              args: {
                suggestions: ["🚀 Setup My Zaps"],
              },
              state: "result" as const,
              result: {
                success: true,
                message: "Showing build mode suggestions",
              },
            },
          ],
        },
      ];
    }

    // Default welcome message when no prompt is provided
    return [
      {
        id: "canvas-welcome",
        role: "assistant" as const,
        content:
          "I'm here to help you build and edit zapier workflows on your canvas. I can help you create systems using zaps, tables, interfaces, and chatbots. I can also edit existing components and work with Zaps.\n\nWhat would you like to do?",
      },
    ];
  };

  // Feature-flagged migration: Use ai-toolkit when aiCobuildCopilot is enabled
  const adaptedTools = adaptCanvasToolsToAiToolkit(filteredTools(canvasTools));

  return (
    <CopilotProvider
      api="/api/canvasui/chat/completionsv2"
      clientTools={adaptedTools}
      copilotId={toggles.aiCopilotHandoff ? "canvas" : undefined}
      registry={registry && toggles.aiCopilotHandoff ? registry : undefined}
      maxSteps={10}
      // Use Claude 4 Sonnet for field mapping tools, default GPT-4.1 for other tools
      headers={
        shouldShowBuildModeSuggestions()
          ? { "X-Model": "claude4sonnet" }
          : undefined
      }
      body={{
        additionalData: {
          zsl: zsl || {},
          canvas: canvas || {},
          nodes: getNodesForContext(),
          edges,
        },
      }}
      initialMessages={generateInitialMessages()}
      onError={(error: any) => {
        console.error("Canvas copilot error:", error);
      }}
    >
      {children}
    </CopilotProvider>
  );
}
